import { getIsStatus } from '@/Components/posts/status';
import { type Hub, PostStatusEnum } from '@/graphql/types';
import type { AppStore } from '@/inertia/AppStore';
import { useEffect, useRef } from 'react';
import { chatbotInstance } from './chatbot';
import { chatbotStateManager } from './chatbotStateManager';
import type { CustomGreetingPayload } from './customElements/greeting';
import type { DFMessenger } from './messenger.types';

type Props = {
  pageType: AppStore['pageType'];
  znglist?: AppStore['znglist'];
  hub?: AppStore['hub'];
};

export const __useDfMessenger__ = (props: Props) => {
  const df = useRef<DFMessenger>(document.querySelector('df-messenger'));

  const initPageType = useRef(props.pageType);
  const initId = useRef(props.znglist?.id || props.hub?.id);
  const isPrivateContent = useRef(false);

  // Check if current content is private/hidden
  const checkIfPrivateContent = () => {
    if (props.pageType === 'znglist' && props.znglist) {
      return getIsStatus(props.znglist.status, [
        PostStatusEnum.Private,
        PostStatusEnum.Hidden,
        PostStatusEnum.HiddenBS,
      ]);
    }
    if (props.pageType === 'hub' && props.hub) {
      return getIsStatus(props.hub.status, [PostStatusEnum.Private, PostStatusEnum.Hidden, PostStatusEnum.HiddenBS]);
    }
    return false;
  };

  // Generate a unique session ID for private content
  const generateSessionId = (pageType: string, contentId?: string) => {
    return `${pageType}-${contentId || 'default'}-${Date.now()}`;
  };

  // Check if we need a new session
  const needsNewSession = () => {
    const currentIsPrivate = checkIfPrivateContent();
    const currentId = props.znglist?.id || props.hub?.id;

    return chatbotStateManager.shouldStartNewSession(props.pageType as string, currentId || null, currentIsPrivate);
  };

  // Check if greeting should be shown
  const shouldShowGreeting = () => {
    // Check if greeting already exists in DOM
    if (chatbotInstance.hasGreetingInDOM()) {
      return false;
    }

    // If we need a new session, we should show greeting
    if (needsNewSession()) {
      return true;
    }

    // Otherwise, only show if we haven't shown it yet
    return !chatbotInstance.hasGreetingBeenShown();
  };

  useEffect(() => {
    if (!df.current) {
      return;
    }

    const currentIsPrivate = checkIfPrivateContent();
    const currentId = props.znglist?.id || props.hub?.id;

    // Initialize args with updated default filter
    const args = {
      title: 'Zngly',
      message: 'How can I assist you today?',
      filter: [
        {
          dataStores: [
            `projects/zngly-corporate/locations/global/collections/default_collection/dataStores/${window.znRuntime?.dfDsId || ''}`,
          ],
          // Include hiddenbs in default filter
          filter: `post_status: ANY("publish", "hidden_b_s")`,
        },
      ],
    };

    const decodeRelayId = (id?: string | null | undefined) => {
      if (!id) return null;

      if (Number.isInteger(Number(id))) {
        return Number(id).valueOf();
      }

      try {
        const decoded = atob(id);
        const parts = decoded.split(':');
        return parts?.length === 2 ? Number(parts[1]).valueOf() : null;
      } catch (error) {
        return null;
      }
    };

    // Handle private content sessions
    if (needsNewSession()) {
      // Start new session for private content
      const sessionId = generateSessionId(props.pageType as string, currentId);
      chatbotInstance.setSessionId(sessionId);
      chatbotInstance.resetGreeting();

      // Clear messages and start fresh session
      df.current.startNewSession({ retainHistory: false });

      // Update state manager
      chatbotStateManager.updatePageContext(props.pageType as string, currentId || null, currentIsPrivate);
    }

    // Configure filters based on page type
    if (props.pageType === 'znglist') {
      args.title = props.znglist?.title || args.title;

      const nodes = props.znglist?.znglist?.znglyContents ?? [];
      const nodesWithId = nodes.map((node) => decodeRelayId(node?.id)).filter((id) => !!id) as number[];

      const filter = nodesWithId.map((id) => `post_id = ${id}`).join(' OR ');

      if (getIsStatus(props?.znglist?.status, [PostStatusEnum.Private, PostStatusEnum.Hidden])) {
        args.message =
          'How can I assist you with this Znglist. **Note**: you can only ask about the content on this page.';

        // Only show content from this specific znglist
        args.filter[0].filter = `(${filter})`;
      } else if (getIsStatus(props?.znglist?.status, [PostStatusEnum.HiddenBS])) {
        // For HiddenBS, include both the specific content and other searchable content
        args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s") OR (${filter})`;
      } else {
        // Public znglist - include everything
        args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s") OR (${filter})`;
      }
    } else if (props.pageType === 'hub') {
      args.title = props.hub?.title || args.title;

      // Get the ids from the hubPage node
      const ids = [] as number[];

      props?.hub?.connectionHubToHps?.edges?.forEach((edge) => {
        if (!edge?.node) return;

        const hubPageNode = edge?.node as NonNullable<NonNullable<Hub['connectionHubToHps']>['edges']>[number]['node'];
        hubPageNode.hubPage?.node?.connectionHpToCs?.edges?.forEach((edge) => {
          if (!edge?.node) return;
          const catalogNode = edge?.node as NonNullable<
            NonNullable<typeof hubPageNode.hubPage.node>['connectionHpToCs']
          >['edges'][number]['node'];

          const catalogId = decodeRelayId(catalogNode?.catalogId);
          if (catalogId) {
            ids.push(catalogId);
          }
        });
      });

      const uniqueIds = Array.from(new Set(ids)); // Remove duplicates
      const filter = uniqueIds.map((id) => `post_id = ${id}`).join(' OR ');

      if (getIsStatus(props?.hub?.status, [PostStatusEnum.Private, PostStatusEnum.Hidden])) {
        args.message = 'How can I assist you with this Hub. **Note**: you can only ask about the content on this page.';
        args.filter[0].filter = `(${filter})`;
      } else if (getIsStatus(props?.hub?.status, [PostStatusEnum.HiddenBS])) {
        // For HiddenBS hubs, include both specific and searchable content
        args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s") OR (${filter})`;
      } else {
        args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s") OR (${filter})`;
      }
    } else if (props.pageType === 'front_page') {
      args.title = 'the AI Assistant (beta)';
      args.message = 'How can I assist you today?';

      // Default filter for front page with hidden_b_s
      args.filter[0].filter = `post_status: ANY("publish", "hidden_b_s")`;
    } else {
      // For all other page types, just update the filter
      df.current.setQueryParameters({
        searchConfig: {
          filterSpecs: args.filter,
          boostSpecs: [],
        },
      });
      return;
    }
    // TODO: This code is commented out because starting a new session is currently not required. Uncomment if session management changes in the future.

    // Update query parameters
    df.current.setQueryParameters({
      searchConfig: {
        filterSpecs: args.filter,
        boostSpecs: [],
      },
    });

    // Only show greeting if we should
    if (shouldShowGreeting()) {
      // Wait for chat to be ready and then show greeting
      chatbotInstance.waitForChatReady().then((isReady) => {
        if (!isReady) {
          console.warn('Chat not ready for greeting');
          return;
        }

        // Double-check that greeting still hasn't been shown
        if (chatbotInstance.hasGreetingInDOM() || chatbotInstance.hasGreetingBeenShown()) {
          return;
        }

        const messenger = chatbotInstance.getDFMessenger() || df.current;
        if (!messenger) return;

        messenger.renderCustomCard([
          {
            type: 'custom_template',
            name: 'df-custom-greeting',
            payload: JSON.stringify({
              greeting: {
                title: `Welcome to ${args.title}!`,
                message: args.message,
                // icon: 'https://example.com/icon.png', // Replace with your icon URL
              },
            } as CustomGreetingPayload),
          },
        ]);

        // Mark greeting as shown
        chatbotInstance.markGreetingAsShown();
      });
    }

    // Update refs and state for next comparison
    initPageType.current = props.pageType;
    isPrivateContent.current = currentIsPrivate;
    initId.current = currentId;

    // Always update the current page context
    chatbotStateManager.updatePageContext(props.pageType, currentId || null, currentIsPrivate);
  }, [props.pageType, props.hub?.id, props.znglist?.id]);
};
